import * as R from "ramda";

import { logError } from "../../utils/error";
import { getTimeRangeFilter } from "../../utils/timerange";
import { TimeRange } from "../../utils/timerange";
import { supabase } from "../supabase/client";

const ALL = "ALL";

interface AggregatedLensPerformanceStatsParams {
  timeRange: TimeRange;
  timezone: string;
  userFilter?: string;
  lensFilter?: string;
  permittedEmails?: string[] | null;
}

export interface AggregatedLensPerformanceStatsData {
  lens_name: string;
  user_email: string;
  min_score: number;
  max_score: number;
  avg_score: number;
  stddev_score: number;
  median_score: number;
  scored_count: number;
  not_scored_count: number;
  rank: number;
  percentile: number;
  prev_avg_score: number;
  delta_vs_prev_avg: number;
  pct_of_prev_avg: number;
  pct_delta_vs_prev_avg: number;
  group_min_score: number;
  group_max_score: number;
  group_avg_score: number;
  group_stddev_score: number;
  group_median_score: number;
  group_scored_count: number;
  group_not_scored_count: number;
  delta_vs_group_avg: number;
  pct_of_group_avg: number;
  pct_delta_vs_group_avg: number;
  best_user_email: string;
  best_user_avg_score: number;
  delta_vs_best: number;
  pct_of_best: number;
  pct_delta_vs_best: number;
}

export type AggregatedLensPerformanceStatsDataByLens = Omit<AggregatedLensPerformanceStatsData, "userEmail">;
export type AggregatedLensPerformanceStatsDataByUser = Omit<AggregatedLensPerformanceStatsData, "lensName">;
export type AggregatedLensPerformanceStatsDataAll = Omit<AggregatedLensPerformanceStatsData, "lensName" | "userEmail">;

export interface AggregatedLensPerformanceStats {
  all: AggregatedLensPerformanceStatsDataAll | null;
  byLens: AggregatedLensPerformanceStatsDataByLens[];
  byUser: AggregatedLensPerformanceStatsDataByUser[];
  byLensAndUser: AggregatedLensPerformanceStatsData[];
}

const genAggregatedLensPerformanceStats = (data: AggregatedLensPerformanceStatsData[]): AggregatedLensPerformanceStats => {
  return {
    all: R.head(R.filter(row => row.lens_name === ALL && row.user_email === ALL, data)) || null,
    byLens: R.filter(row => (row.user_email === ALL && row.lens_name !== ALL), data),
    byUser: R.filter(row => (row.lens_name === ALL && row.user_email !== ALL), data),
    byLensAndUser: R.filter(row => row.lens_name !== ALL && row.user_email !== ALL, data)
  };
};

export interface LensPerformanceSparklineParams {
  userFilter?: string;
  lensFilter?: string;
  permittedEmails?: string[] | null;
}

export interface LensSparklineData {
  lens_name: string;
  scores: (number | null)[];
}

export interface UserSparklineData {
  user_email: string;
  scores: (number | null)[];
}

export interface LensPerformanceSparklineResponse {
  byLens: LensSparklineData[];
  byUser: UserSparklineData[];
}

export const getLensPerformanceSparklineData = async ({
  userFilter = "all",
  lensFilter = "all",
  permittedEmails
}: LensPerformanceSparklineParams): Promise<LensPerformanceSparklineResponse> => {
  try {
    const { data, error } = await supabase.rpc("get_lens_performance_sparkline_data", {
      user_filter: userFilter === "all" ? null : [userFilter],
      lens_filter: lensFilter === "all" ? null : [lensFilter],
      permitted_emails: permittedEmails ?? undefined
    } as any);

    if (error) {
      throw new Error(`Failed to fetch lens performance sparkline data: ${error.message}`);
    }

    return (data as unknown as LensPerformanceSparklineResponse) || { byLens: [], byUser: [] };
  } catch (error) {
    logError("Error fetching lens performance sparkline data", error);
    throw error;
  }
};

export const getAggregatedLensPerformanceStats = async ({
  timeRange,
  timezone,
  userFilter = "all",
  lensFilter = "all",
  permittedEmails
}: AggregatedLensPerformanceStatsParams): Promise<AggregatedLensPerformanceStats> => {
  try {
    const timeRangeParams = getTimeRangeFilter(timeRange, timezone);

    const { data, error } = await supabase.rpc("get_aggregated_lens_performance_stats", {
      start_date: timeRangeParams.startDate,
      end_date: timeRangeParams.endDate,
      user_filter: userFilter === "all" ? null : [userFilter],
      lens_filter: lensFilter === "all" ? null : [lensFilter],
      permitted_emails: permittedEmails ?? undefined
    } as any);

    if (error) {
      throw new Error(`Failed to fetch aggregated lens performance stats: ${error.message}`);
    }

    const result = genAggregatedLensPerformanceStats(data || []);
    return result;
  } catch (error) {
    logError("Error fetching aggregated lens performance stats", error);
    throw error;
  }
};

export interface LensPerformanceTimelineParams {
  timeRange: TimeRange;
  timezone: string;
  lensName: string;
  userFilter?: string;
  permittedEmails?: string[] | null;
}

export interface LensPerformanceTimelineDataPoint {
  user_email: string;
  date: string;
  min_score: number;
  max_score: number;
  avg_score: number;
  stddev_score: number;
  median_score: number;
  q1_score: number;
  q3_score: number;
  scored_count: number;
  not_scored_count: number;
  avg_score_5d_ma: number | null;
}

export interface LensPerformanceTimelineData {
  all: LensPerformanceTimelineDataPoint[];
  byUser: LensPerformanceTimelineDataPoint[];
}

const genLensPerformanceTimelineData = (data: LensPerformanceTimelineDataPoint[]): LensPerformanceTimelineData => {
  return {
    all: R.filter(row => row.user_email === ALL, data),
    byUser: R.filter(row => row.user_email !== ALL, data)
  };
};

export const getLensPerformanceTimelineData = async ({
  timeRange,
  timezone,
  lensName,
  userFilter = "all",
  permittedEmails
}: LensPerformanceTimelineParams): Promise<LensPerformanceTimelineData> => {
  try {
    const timeRangeParams = getTimeRangeFilter(timeRange, timezone);

    const { data, error } = await supabase.rpc("get_lens_performance_timeline_data", {
      start_date: timeRangeParams.startDate,
      end_date: timeRangeParams.endDate,
      lens_name: lensName,
      user_filter: userFilter === "all" ? null : [userFilter],
      permitted_emails: permittedEmails ?? undefined
    } as any);

    if (error) {
      throw new Error(`Failed to fetch lens performance timeline data: ${error.message}`);
    }

    return genLensPerformanceTimelineData((data ?? []) as unknown as LensPerformanceTimelineDataPoint[]);
  } catch (error) {
    logError("Error fetching lens performance timeline data", error);
    throw error;
  }
};

export interface AggregatedAttributePerformanceParams {
  timeRange: TimeRange;
  timezone: string;
  lensFilter: string;
  userFilter?: string;
  permittedEmails?: string[] | null;
  minCalls?: number;
}

export interface AggregatedAttributePerformanceData {
  user_email: string;
  attr_name: string;
  min_score: number;
  max_score: number;
  avg_score: number;
  stddev_score: number;
  median_score: number;
  scored_count: number;
  not_scored_count: number;
  rank: number;
  percentile: number;
  prev_avg_score: number;
  delta_vs_prev_avg: number;
  pct_of_prev_avg: number;
  pct_delta_vs_prev_avg: number;
  group_min_score: number;
  group_max_score: number;
  group_avg_score: number;
  group_stddev_score: number;
  group_median_score: number;
  group_scored_count: number;
  group_not_scored_count: number;
  delta_vs_group_avg: number;
  pct_of_group_avg: number;
  pct_delta_vs_group_avg: number;
  best_user_email: string;
  best_user_avg_score: number;
  delta_vs_best: number;
  pct_of_best: number;
  pct_delta_vs_best: number;
}

export interface AggregatedAttributePerformanceResponse {
  all: AggregatedAttributePerformanceData[];
  byUser: AggregatedAttributePerformanceData[];
}

const genAggregatedAttributePerformanceStats = (data: AggregatedAttributePerformanceData[]): AggregatedAttributePerformanceResponse => {
  return {
    all: R.filter(row => row.user_email === ALL, data),
    byUser: R.filter(row => row.user_email !== ALL, data)
  };
};

export const getAggregatedAttributePerformanceStats = async ({
  timeRange,
  timezone,
  lensFilter,
  userFilter = "all",
  permittedEmails,
  minCalls = 10
}: AggregatedAttributePerformanceParams): Promise<AggregatedAttributePerformanceResponse> => {
  try {
    const timeRangeParams = getTimeRangeFilter(timeRange, timezone);

    const { data, error } = await supabase.rpc("get_aggregated_attribute_performance_stats", {
      start_date: timeRangeParams.startDate,
      end_date: timeRangeParams.endDate,
      lens_filter: lensFilter,
      user_filter: userFilter === "all" ? null : [userFilter],
      permitted_emails: permittedEmails ?? undefined,
      min_calls: minCalls
    } as any);

    if (error) {
      throw new Error(`Failed to fetch aggregated attribute performance stats: ${error.message}`);
    }

    const result = genAggregatedAttributePerformanceStats(data || []);
    return result;
  } catch (error) {
    logError("Error fetching aggregated attribute performance stats", error);
    throw error;
  }
};
